'use client';

import { useState } from 'react';
import MiniChart from 'src/components/MiniChart';
import ShowHideAccountValuesButton from 'src/components/ShowHideAccountValuesButton/ShowHideAccountValuesButton';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { ZERO } from '@drift-labs/sdk';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import PnlChart from 'src/components/PnlChart';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { ChevronUp } from '@drift-labs/icons';
import usePortfolioHeaderData from 'src/components/PortfolioPage/PortfolioHeader/hooks/usePortfolioHeaderData';
import PortfolioHeaderBalanceChange from 'src/components/PortfolioPage/PortfolioHeader/PortfolioHeaderBalanceChange';
import PortfolioHeaderButtons from 'src/components/PortfolioPage/PortfolioHeader/PortfolioHeaderButtons';
import { cj } from 'src/utils/classNames';
import {
	PORTFOLIO_DEFAULT_PERIOD,
	PORTFOLIO_TOOLTIP_COLLISION_PADDING,
} from 'src/components/PortfolioPage/consts';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import usePortfolioNetAccountValue from 'src/components/PortfolioPage/PortfolioHeader/hooks/usePortfolioNetAccountValue';
import ValueWithLabelCard from 'src/components/primitives/ValueWithLabelCard/ValueWithLabelCard';
import PortfolioHeaderTotalFunding from 'src/components/PortfolioPage/PortfolioHeader/PortfolioHeaderTotalFunding';
import PortfolioHeaderAccountHealth from 'src/components/PortfolioPage/PortfolioHeader/PortfolioHeaderAccountHealth';

export default function PortfolioHeader() {
	const isMobileScreenSize = useIsMobileScreenSize();
	const currentAccountName = useDriftAccountStore(
		(s) => s.getCurrentUserAccount()?.name
	);

	const [period, setPeriod] = useSyncLocalStorage(
		'portfolioPeriod',
		PORTFOLIO_DEFAULT_PERIOD
	);

	const [showExpandedChart, setShowExpandedChart] = useState(false);

	const netAccountValue = usePortfolioNetAccountValue();

	const {
		data: {
			accountValueChange,
			accountValueChangePct,
			miniChartData,
			expandedChartData,
		},
		isLoading,
		isPlaceholderData,
	} = usePortfolioHeaderData({
		period,
		netAccountValue: netAccountValue?.toNum(),
	});

	return (
		<div className="flex flex-wrap items-center justify-between gap-y-lg py-md sm:gap-y-2xl sm:py-2xl">
			<div className="flex basis-full flex-col gap-y-sm sm:basis-2/5">
				<div className="flex items-center gap-x-sm">
					<h1 className="typo-b3 whitespace-nowrap text-text-secondary">
						<DefinitionTooltip
							definitionId="portfolioAccountValue"
							collisionPadding={PORTFOLIO_TOOLTIP_COLLISION_PADDING}
						>
							Account Value
						</DefinitionTooltip>{' '}
						{currentAccountName ? `- ${currentAccountName}` : ''}
					</h1>
					<ShowHideAccountValuesButton size="lg" />
				</div>
				<div className="flex w-full items-center gap-x-8 sm:h-18">
					<div className="flex w-full flex-col gap-x-lg gap-y-sm sm:flex-row sm:items-center">
						{/* Account value display. */}
						<NumberDisplay
							value={netAccountValue}
							type="currency"
							className="min-w-36 typo-h3 text-text-default"
							skeletonClassName="h-9 sm:h-[38px] w-20"
							isAccountValueToHide
						/>

						{/* Period select and balance change display. */}
						<PortfolioHeaderBalanceChange
							accountValueChange={accountValueChange}
							accountValueChangePct={accountValueChangePct}
							isLoading={isLoading}
							isPlaceholderData={isPlaceholderData}
							period={period}
							setPeriod={setPeriod}
						/>

						{/* The full PnL chart is shown above the CTAs on mobile. */}
						{isMobileScreenSize && (
							<PnlChart
								className="min-h-38"
								data={expandedChartData}
								field="accountValue"
								label="Balance"
								id="balance-chart"
								isHourly={period === '24h'}
								useNeutralColor
								showDriftLogo
								disableAxes
								noGrid
							/>
						)}
					</div>

					{/* The mini chart is only shown on desktop. */}
					{!isMobileScreenSize && !showExpandedChart && (
						<button
							className={cj(
								'w-full hover:opacity-50',
								isPlaceholderData && 'animate-pulse'
							)}
							onClick={() => setShowExpandedChart(true)}
						>
							<MiniChart
								data={miniChartData}
								maxHeight={74}
								fixedWidth={200}
								useNeutralColor
							/>
						</button>
					)}
				</div>
			</div>

			<div className="flex flex-col gap-y-lg max-sm:w-full">
				<ValueWithLabelCard.Container>
					<PortfolioHeaderTotalFunding />
					<PortfolioHeaderAccountHealth />
				</ValueWithLabelCard.Container>

				{/* Deposit, Withdraw, Transfer CTAs */}
				<PortfolioHeaderButtons />
			</div>

			{/* The full PnL chart is shown below everything on desktop. */}
			{!isMobileScreenSize && showExpandedChart && (
				<>
					<div className="flex w-full justify-center">
						<IconButton
							size="sm"
							className="self-center"
							icon={ChevronUp}
							onClick={() => setShowExpandedChart(false)}
						/>
					</div>

					<div
						className={cj(
							'w-full rounded-sm border border-stroke-divider bg-container-bg p-2xl',
							isPlaceholderData && 'animate-pulse'
						)}
					>
						<PnlChart
							className="min-h-62"
							data={expandedChartData}
							field="accountValue"
							label="Balance"
							id="balance-chart"
							positive={accountValueChange ? accountValueChange.gt(ZERO) : true}
							isHourly={period === '24h'}
							isLoading={isPlaceholderData}
							useNeutralColor
							showDriftLogo
						/>
					</div>
				</>
			)}
		</div>
	);
}
